import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { type PathLink, usePathHelper } from '@/hooks/usePathHelper'
import { cn, parseCurrency } from '@/lib/utils'
import {
  type SalesSummaryReportSearch,
  getSalesSummaryGranularityOptionsForRange,
} from '@/lib/services/reports/reports.schema'
import type {
  ReportDateMode,
  SalesSummaryReport,
  SalesSummaryReportCustomerRow,
} from '@/lib/services/reports/reports.types'
import { BarChart3 } from 'lucide-react'
import { ChannelMixCard, FulfillmentMixCard } from './-mix-cards'
import { hasProvidedCustomer, hasSalesSummaryActivity } from './-helpers'
import {
  KeyCustomersCard,
  PaymentMethodsCard,
  TopOrganizationsCard,
  TopProductsCard,
} from './-leaderboard-cards'
import {
  SalesSummaryPrimaryMetrics,
  SalesSummaryFinancialBreakdown,
  SalesStatisticsSalesBreakdown,
} from './-summary-cards'
import { SalesSummaryTrendCard } from './-trend-card'
import { OrderActivityCard } from './-order-activity'
import { TransactionsCard } from './-transactions-card'

type ReportDateSearch = {
  start_date: string
  end_date: string
  date_mode?: ReportDateMode
}

type SalesSummaryReportResultsProps = {
  report: SalesSummaryReport
  orgId: number
  routeLink: PathLink
  search: SalesSummaryReportSearch
  onGranularityChange: (granularity: string) => void
}

export function SalesSummaryReportResults({
  report,
  orgId,
  routeLink,
  search,
  onGranularityChange,
}: SalesSummaryReportResultsProps) {
  const path = usePathHelper()
  const isGlobalReport = Boolean(report.is_global)
  const isOrderBased = report.basis === 'orders'
  const summary = report.summary
  const timeSeries = report.time_series ?? []
  const fulfillmentRows = report.breakdowns.fulfillment_types ?? []
  const sourceRows = report.breakdowns.sources ?? []
  const paymentRows = report.breakdowns.payment_methods ?? []
  const productRows = report.product_results ?? []
  const organizationRows = report.organization_results ?? []
  const customerRows = sortRowsByActivity(
    isGlobalReport ? [] : (report.customer_information ?? []).filter(hasProvidedCustomer),
    'total_sales'
  )
  const customerCount = customerRows.length
  const customerSalesTotal = customerRows.reduce(
    (sum, row) => sum + parseCurrency(row.total_sales),
    0
  )
  const organizationSalesTotal = organizationRows.reduce(
    (sum, row) => sum + parseCurrency(row.total_sales),
    0
  )
  const transactionMeta = report.meta
  const filters = report.filters
  const startDate = filters.start_date
  const endDate = filters.end_date
  const dateMode = filters.date_mode
  const granularity = filters.granularity ?? 'monthly'
  const timezone = filters.timezone
  const selectedFulfillmentType = filters.fulfillment_type ?? null
  const selectedSource = filters.source ?? null
  const hasReportData = hasSalesSummaryActivity(report)
  const showFulfillmentMix = !selectedFulfillmentType
  const showChannelMix = !selectedSource
  const reportDateSearch = buildReportDateSearch({ startDate, endDate, dateMode })
  const salesTaxLink =
    !isGlobalReport && reportDateSearch
      ? path.salesTax.index().using({ orgId }).search(reportDateSearch).link
      : null
  const tipsReportLink =
    !isGlobalReport && reportDateSearch
      ? path.tips.index().using({ orgId }).search(reportDateSearch).link
      : null
  const salesSummaryLink =
    isOrderBased && reportDateSearch
      ? path.salesSummary
          .index()
          .using({ orgId })
          .search({ start_date: reportDateSearch.start_date, end_date: reportDateSearch.end_date })
          .link
      : null
  const depositsLink =
    isOrderBased && reportDateSearch
      ? path.deposits.index().using({ orgId }).search(reportDateSearch).link
      : null
  const hasSidebarBreakdowns = showFulfillmentMix || showChannelMix
  const hasInsightCards =
    !isGlobalReport ||
    productRows.length > 0 ||
    (isGlobalReport ? organizationRows.length > 0 || hasReportData : customerRows.length > 0)
  const showBothMixCards = showFulfillmentMix && showChannelMix
  const granularityOptions = getSalesSummaryGranularityOptionsForRange({
    start_date: startDate,
    end_date: endDate,
  })

  if (!hasReportData) {
    return (
      <Card>
        <CardContent className="py-12">
          <EmptySalesSummaryState />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="@container/sales-results space-y-4">
      <SalesSummaryPrimaryMetrics
        summary={summary}
        isOrderBased={isOrderBased}
        customerCount={customerCount}
        customerSalesTotal={customerSalesTotal}
        isGlobalReport={isGlobalReport}
        organizationCount={organizationRows.length}
        organizationSalesTotal={organizationSalesTotal}
      />

      <div
        className={cn(
          'space-y-4 min-[1540px]:grid min-[1540px]:grid-cols-[minmax(0,2fr)_minmax(380px,1fr)] min-[1540px]:gap-4 min-[1540px]:space-y-0',
          isOrderBased ? 'min-[1540px]:items-start' : 'min-[1540px]:items-stretch'
        )}
      >
        <div
          className={cn(
            'space-y-4',
            !isOrderBased && 'min-[1540px]:flex min-[1540px]:h-[890px] min-[1540px]:flex-col'
          )}
        >
          <SalesSummaryTrendCard
            data={timeSeries}
            isGlobalReport={isGlobalReport}
            isOrderBased={isOrderBased}
            granularity={granularity}
            granularityOptions={granularityOptions}
            timezone={timezone}
            onGranularityChange={onGranularityChange}
          />

          {hasSidebarBreakdowns ? (
            <div className="grid gap-4 xl:flex-1 xl:grid-cols-2 xl:items-stretch">
              {showFulfillmentMix ? (
                <FulfillmentMixCard
                  rows={fulfillmentRows}
                  isGlobalReport={isGlobalReport}
                  isOrderBased={isOrderBased}
                  fullHeight={showBothMixCards}
                  className={cn(!showChannelMix && 'xl:col-span-2')}
                />
              ) : null}

              {showChannelMix ? (
                <ChannelMixCard
                  rows={sourceRows}
                  isGlobalReport={isGlobalReport}
                  isOrderBased={isOrderBased}
                  fullHeight={showBothMixCards}
                  className={cn(!showFulfillmentMix && 'xl:col-span-2')}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <SalesStatisticsSalesBreakdown
          breakdown={report.sales_breakdown}
          salesSummaryLink={salesSummaryLink}
          salesTaxLink={salesTaxLink}
          tipsReportLink={tipsReportLink}
          depositsLink={depositsLink}
        />

        <SalesSummaryFinancialBreakdown
          summary={summary}
          financialBreakdown={report.financial_breakdown}
          isGlobalReport={isGlobalReport}
          salesTaxLink={salesTaxLink}
          tipsReportLink={tipsReportLink}
          className="min-[1540px]:flex min-[1540px]:h-[890px] min-[1540px]:flex-col min-[1540px]:overflow-hidden"
        />
      </div>

      {hasInsightCards ? (
        <div
          className={cn(
            'grid gap-4 @3xl/sales-results:grid-cols-2',
            !isGlobalReport && '@5xl/sales-results:grid-cols-3'
          )}
        >
          {isGlobalReport && organizationRows.length > 0 ? (
            <TopOrganizationsCard
              organizationRows={organizationRows}
              reportDateSearch={reportDateSearch}
            />
          ) : null}
          <TopProductsCard
            productRows={productRows}
            title={
              isGlobalReport
                ? 'OneStem Product Performance'
                : isOrderBased
                  ? 'By Product Type'
                  : 'Top Products'
            }
            isOrderBased={isOrderBased}
            description={
              isGlobalReport
                ? 'Global products with the largest sales impact'
                : isOrderBased
                  ? null
                  : 'Amounts paid toward products'
            }
            emptyMessage={
              isGlobalReport
                ? 'No curated product sales in the selected range.'
                : 'No products in the selected range.'
            }
          />
          {!isGlobalReport ? (
            <KeyCustomersCard
              customerRows={customerRows}
              orgId={orgId}
              isOrderBased={isOrderBased}
            />
          ) : null}
          {!isGlobalReport ? (
            <PaymentMethodsCard rows={paymentRows} isOrderBased={isOrderBased} />
          ) : null}
        </div>
      ) : null}

      {report.basis === 'orders' ? (
        <OrderActivityCard rows={report.rows ?? []} orgId={orgId} timezone={timezone} />
      ) : (
        <TransactionsCard
          rows={report.rows ?? []}
          meta={transactionMeta}
          orgId={orgId}
          timezone={timezone}
          isGlobalReport={isGlobalReport}
          filters={filters}
          routeLink={routeLink}
          search={search}
        />
      )}
    </div>
  )
}

function EmptySalesSummaryState() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <BarChart3 className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
      </div>
      <Typography variant="h4" tone="muted">
        No orders or refunds in the selected date range
      </Typography>
    </div>
  )
}

function sortRowsByActivity(
  rows: SalesSummaryReportCustomerRow[],
  key: 'total_sales' | 'net_profit'
) {
  return [...rows].sort((left, right) => {
    const rightActivity = Math.abs(parseCurrency(right[key]))
    const leftActivity = Math.abs(parseCurrency(left[key]))
    const activityDifference = rightActivity - leftActivity

    if (activityDifference !== 0) {
      return activityDifference
    }

    return parseCurrency(right[key]) - parseCurrency(left[key])
  })
}

function buildReportDateSearch({
  startDate,
  endDate,
  dateMode,
}: {
  startDate: string
  endDate: string
  dateMode: ReportDateMode
}): ReportDateSearch | null {
  if (!startDate || !endDate) {
    return null
  }

  return {
    start_date: startDate,
    end_date: endDate,
    ...(dateMode ? { date_mode: dateMode } : {}),
  }
}
